import {
    Badge,
    Button,
    Drawer,
    EmptyState,
    Logo,
    Sidebar,
    SidebarBrand,
    SidebarFooter,
    SidebarGroup,
    SidebarItem,
    SidebarNav,
    ThemeToggle,
    Tooltip,
    buttonVariants,
    cn,
} from '@platform/ui';
import { Link, Outlet, useMatches, useRouterState } from '@tanstack/react-router';
import { Lock, Menu, ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useMessages } from '../i18n';
import { useOwnStoreName } from '../lib/app-config';
import { useFormat } from '../lib/format';
import { useNotifications } from '../lib/notifications';
import { usePageTitle } from '../lib/page-title';
import { useCanOpen, useLogout, useSession, type PagePath } from '../lib/session';
import { GlobalSearch } from './GlobalSearch';
import { NavIcon } from './icons';
import { LanguageToggle } from './LanguageToggle';
import { onRoute, sections } from './SectionTabs';
import { SubscriptionBanner } from './SubscriptionBanner';
import { VerifyEmailBanner } from './VerifyEmailBanner';

/**
 * Navigation, declared as data and grouped by the job it belongs to (docs/11 §8).
 *
 * The menu is the map of a seller's day, in the order the day runs: sales, then
 * the catalogue, the channels and the shop. The groups are always open — a label
 * above its entries, not a control to click — and the whole map fits a laptop
 * screen without scrolling, so nothing has to be hunted for. Routes that are
 * sections of one job are one entry, active on each of its tabs.
 *
 * Each entry names the routes it covers, the message key for its label and its
 * icon, never the label itself: a hard-coded string could not be translated.
 * What a route needs is its own `staticData.permission` in main.tsx. An entry
 * leads to the first of its routes the person may open and is hidden when there
 * is none; a group whose every entry is hidden renders nothing — no empty
 * heading. Hiding an entry is presentation, not access control: the shell
 * refuses the page and the server refuses the request regardless, and offering
 * a menu item that always fails is simply a worse experience.
 */
const navGroups = [
    {
        titleKey: null,
        items: [{ labelKey: 'dashboard', icon: 'dashboard', routes: [{ to: '/' }] }],
    },
    {
        titleKey: 'sales',
        items: [
            { labelKey: 'orders', icon: 'orders', routes: [{ to: '/siparisler' }] },
            { labelKey: 'claims', icon: 'claims', routes: [{ to: '/iadeler' }] },
            { labelKey: 'shipping', icon: 'shipping', routes: [{ to: '/kargo' }] },
            { labelKey: 'customers', icon: 'customers', routes: [{ to: '/musteriler' }] },
            { labelKey: 'questions', icon: 'questions', routes: [{ to: '/sorular' }] },
            { labelKey: 'invoices', icon: 'invoices', routes: [{ to: '/faturalar' }] },
            { labelKey: 'finance', icon: 'finance', routes: sections.finance.tabs },
        ],
    },
    {
        titleKey: 'catalog',
        items: [
            {
                labelKey: 'products',
                icon: 'products',
                routes: [{ to: '/urunler' }],
                // Bulk editing is opened from the product list, so the list's entry
                // stays lit while it is on screen.
                also: ['/toplu-guncelleme'],
            },
            { labelKey: 'brands', icon: 'brands', routes: [{ to: '/markalar' }] },
            { labelKey: 'inventory', icon: 'inventory', routes: sections.stock.tabs },
            { labelKey: 'pricing', icon: 'pricing', routes: [{ to: '/fiyatlandirma' }] },
            { labelKey: 'suppliers', icon: 'suppliers', routes: [{ to: '/tedarikciler' }] },
        ],
    },
    {
        titleKey: 'channels',
        items: [
            { labelKey: 'channels', icon: 'channels', routes: [{ to: '/kanallar' }] },
            { labelKey: 'mapping', icon: 'mapping', routes: sections.mapping.tabs },
        ],
    },
    {
        titleKey: 'storefront',
        items: [
            { labelKey: 'storefrontSettings', icon: 'storefront', routes: [{ to: '/magaza' }] },
            { labelKey: 'coupons', icon: 'coupons', routes: [{ to: '/kuponlar' }] },
            { labelKey: 'promotions', icon: 'promotions', routes: [{ to: '/otomatik-indirimler' }] },
            { labelKey: 'sizeGuides', icon: 'sizeGuides', routes: [{ to: '/beden-tablolari' }] },
            { labelKey: 'reviews', icon: 'reviews', routes: [{ to: '/yorumlar' }] },
            { labelKey: 'newsletter', icon: 'newsletter', routes: [{ to: '/bulten' }] },
        ],
    },
] as const;

// The account row, pinned under the menu as icons: running the account is not the
// day's selling, and six labelled rows there would cost the menu its fit.
const accountItems = [
    // The person's own account rather than the company's: its route needs no
    // permission, so every signed-in person can reach it.
    { labelKey: 'account', icon: 'account', routes: [{ to: '/hesabim' }] },
    { labelKey: 'team', icon: 'team', routes: [{ to: '/ekip' }] },
    { labelKey: 'subscription', icon: 'subscription', routes: [{ to: '/abonelik' }] },
    { labelKey: 'developer', icon: 'developer', routes: sections.developer.tabs },
    { labelKey: 'support', icon: 'support', routes: [{ to: '/destek' }] },
] as const;

/** An icon-only destination: its name is its accessible label, repeated in a tooltip. */
const iconDestination = cn(
    buttonVariants({ variant: 'ghost', size: 'icon' }),
    'relative text-muted-foreground aria-[current=page]:bg-primary-subtle aria-[current=page]:text-foreground [&[aria-current=page]_svg]:text-primary',
);

/** Whether the page on screen belongs to an entry: one of its routes or a page under one. */
function isCurrent(pathname: string, entry: { routes: readonly { to: string }[]; also?: readonly string[] }): boolean {
    return [...entry.routes.map((route) => route.to), ...(entry.also ?? [])].some((to) => onRoute(pathname, to));
}

/**
 * The menu and the account row. Rendered twice — in the sidebar and in the phone
 * drawer — so each copy scrolls its own active entry into view.
 */
function Navigation() {
    const t = useMessages();
    const canOpen = useCanOpen();
    const logout = useLogout();
    const ownStoreName = useOwnStoreName();
    const pathname = useRouterState({ select: (state) => state.location.pathname });

    async function onSignOut() {
        try {
            await logout.mutateAsync();
        } catch {
            // Leaving is what the person asked for, so a failed request does not keep them here.
        }
        // A full navigation rather than a client-side one. Signing out clears the
        // query cache, which unmounts this component while a client-side redirect
        // would still be resolving, and the redirect was being dropped. Reloading
        // also guarantees nothing from the previous session survives in memory,
        // which matters on a shared machine.
        window.location.assign('/giris');
    }

    const firstOpen = (routes: readonly { to: PagePath }[]) => routes.find((route) => canOpen(route.to))?.to;

    return (
        <>
            <SidebarNav label={t.nav.menu} path={pathname}>
                {navGroups.map((group) => {
                    if (!group.items.some((item) => firstOpen(item.routes))) return null;
                    return (
                        <SidebarGroup
                            key={group.titleKey ?? 'home'}
                            title={group.titleKey && (group.titleKey === 'storefront' ? ownStoreName : t.navGroups[group.titleKey])}
                        >
                            {group.items.map((item) => {
                                const to = firstOpen(item.routes);
                                if (!to) return null;
                                return (
                                    <SidebarItem key={item.labelKey} icon={<NavIcon name={item.icon} />}>
                                        <Link to={to} aria-current={isCurrent(pathname, item) ? 'page' : undefined} activeOptions={{ exact: true }}>
                                            {t.nav[item.labelKey]}
                                        </Link>
                                    </SidebarItem>
                                );
                            })}
                        </SidebarGroup>
                    );
                })}
            </SidebarNav>

            <SidebarFooter>
                {accountItems.map((item) => {
                    const to = firstOpen(item.routes);
                    if (!to) return null;
                    const label = t.nav[item.labelKey];
                    return (
                        <Tooltip key={item.labelKey} label={label}>
                            <Link
                                to={to}
                                aria-label={label}
                                aria-current={isCurrent(pathname, item) ? 'page' : undefined}
                                activeOptions={{ exact: true }}
                                className={cn(iconDestination, 'w-auto min-w-0 flex-1')}
                            >
                                <NavIcon name={item.icon} />
                            </Link>
                        </Tooltip>
                    );
                })}
                <Tooltip label={t.common.signOut}>
                    <button
                        type="button"
                        aria-label={t.common.signOut}
                        onClick={onSignOut}
                        disabled={logout.isPending}
                        className={cn(iconDestination, 'w-auto min-w-0 flex-1')}
                    >
                        <NavIcon name="signOut" />
                    </button>
                </Tooltip>
            </SidebarFooter>
        </>
    );
}

/** The inbox as a bell in the top bar, carrying what is unread. */
function NotificationsBell() {
    const t = useMessages();
    const format = useFormat();
    const inbox = useNotifications();
    const unread = inbox.data?.unread ?? 0;

    return (
        <Tooltip label={t.nav.notifications} side="bottom" align="end">
            <Link
                to="/bildirimler"
                aria-label={unread > 0 ? `${t.nav.notifications}, ${t.notifications.unreadCount(unread)}` : t.nav.notifications}
                activeOptions={{ exact: true }}
                className={iconDestination}
            >
                <NavIcon name="notifications" />
                {unread > 0 && (
                    <span
                        aria-hidden="true"
                        className="absolute top-1 right-1 min-w-4.5 rounded-full bg-primary px-1 text-center text-12/4.5 font-semibold text-primary-foreground tabular-nums"
                    >
                        {unread > 99 ? '99+' : format.count(unread)}
                    </span>
                )}
            </Link>
        </Tooltip>
    );
}

/**
 * The page for the route on screen, or the refusal when the session lacks the
 * permission the route names. A refused page is never mounted, so none of its
 * requests leave and none of its controls are drawn (docs/11 §8).
 */
function RoutedPage() {
    const { access } = useSession();
    const refused = useMatches({
        select: (matches) => matches.map((match) => match.staticData.permission).find((permission) => access(permission) !== 'open'),
    });

    if (refused === undefined) return <Outlet />;
    // The role allows it and the plan does not: a plan to change, not an owner
    // to ask (ADR 0015).
    return access(refused) === 'not_in_plan' ? <NotInPlan /> : <Refusal />;
}

/**
 * Stands in for a refused page, and names the browser tab for itself. A component
 * of its own rather than a branch of RoutedPage: the page under it names the tab
 * too, and a parent's effect would run after the child's and overwrite it.
 */
function Refusal() {
    const t = useMessages();
    usePageTitle(t.common.forbidden.title);
    return (
        <EmptyState
            titleAs="h1"
            icon={<Lock aria-hidden="true" />}
            title={t.common.forbidden.title}
            description={t.common.forbidden.body}
            action={
                <Button asChild variant="secondary">
                    <Link to="/">{t.common.forbidden.home}</Link>
                </Button>
            }
        />
    );
}

/**
 * Stands in for a page the company's plan leaves out, with the way to the
 * subscription where the person may open it.
 */
function NotInPlan() {
    const t = useMessages();
    const canOpen = useCanOpen();
    usePageTitle(t.common.notInPlan.title);
    return (
        <EmptyState
            titleAs="h1"
            icon={<Lock aria-hidden="true" />}
            title={t.common.notInPlan.title}
            description={t.common.notInPlan.body}
            action={
                <Button asChild variant="secondary">
                    {canOpen('/abonelik') ? (
                        <Link to="/abonelik">{t.common.notInPlan.subscription}</Link>
                    ) : (
                        <Link to="/">{t.common.forbidden.home}</Link>
                    )}
                </Button>
            }
        />
    );
}

export function AppShell() {
    const t = useMessages();
    const { me } = useSession();

    const roleKey = me?.company?.role as keyof typeof t.roles | undefined;
    const roleLabel = roleKey ? t.roles[roleKey] : undefined;

    // The phone menu: the same navigation, in a drawer from the left. It closes
    // on every route change so a tap on a link is one tap, not two.
    const [menuOpen, setMenuOpen] = useState(false);
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });
    useEffect(() => setMenuOpen(false), [pathname]);

    return (
        <>
            {me?.impersonated && (
                // Deliberately impossible to miss: support access that looks like a normal
                // session is how the previous product made misuse undetectable.
                <div
                    role="alert"
                    className="sticky top-0 z-30 flex items-center justify-center gap-2 border-b-2 border-warning bg-warning-subtle px-6 py-2 text-center text-13 font-medium text-warning-strong"
                >
                    <ShieldAlert aria-hidden="true" className="size-4 shrink-0" />
                    {t.impersonation.banner}
                </div>
            )}
            {me && !me.user.emailVerified && <VerifyEmailBanner />}
            {(me?.subscriptionStatus === 'past_due' || me?.subscriptionStatus === 'expired') && <SubscriptionBanner status={me.subscriptionStatus} />}

            <div className="grid min-h-dvh lg:grid-cols-[15rem_minmax(0,1fr)]">
                <Sidebar>
                    <SidebarBrand>
                        <Logo />
                    </SidebarBrand>
                    <Navigation />
                </Sidebar>

                <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} side="left" title={<Logo />} closeLabel={t.common.close}>
                    <div className="-mx-3 -my-6 flex min-h-full flex-col px-3 py-2">
                        <Navigation />
                        <div className="mt-3 flex gap-2 border-t border-border px-3 pt-3">
                            <LanguageToggle />
                            <ThemeToggle labels={t.common.theme} />
                        </div>
                    </div>
                </Drawer>

                <div className="flex min-w-0 flex-col">
                    <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-4 border-b border-border bg-card px-6 max-lg:h-auto max-lg:flex-wrap max-lg:gap-y-2 max-lg:px-4 max-lg:py-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="-ms-2 lg:hidden"
                            aria-label={t.nav.menu}
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen(true)}
                        >
                            <Menu aria-hidden="true" />
                        </Button>

                        <div className="flex min-w-0 items-center gap-2 text-13 text-muted-foreground max-lg:flex-1">
                            <strong className="truncate font-semibold text-foreground">{me?.company?.name}</strong>
                            {roleLabel && <Badge tone="accent">{roleLabel}</Badge>}
                        </div>

                        {/* On a phone the search takes a whole second line of the bar. */}
                        <GlobalSearch className="mx-4 max-w-md flex-1 max-lg:order-3 max-lg:mx-0 max-lg:max-w-none max-lg:basis-full" />

                        <div className="flex items-center gap-2">
                            <NotificationsBell />
                            {/* Language and theme live in the menu drawer on a phone. */}
                            <div className="hidden items-center gap-2 lg:flex">
                                <LanguageToggle />
                                <ThemeToggle labels={t.common.theme} />
                            </div>
                        </div>
                    </header>

                    <main className="mx-auto w-full max-w-page flex-1 px-6 py-8 max-lg:px-4 max-lg:py-6">
                        <RoutedPage />
                    </main>
                </div>
            </div>
        </>
    );
}
